import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, Mic, MessageSquare, Target, Cog, ArrowRight } from "lucide-react";
import jsgLogo from "@/assets/jsg-logo-new.webp";

interface DropdownItem {
  label: string;
  desc: string;
  icon: React.ReactNode;
  href: string;
  badge?: string;
}

const dropdownItems: DropdownItem[] = [
  {
    label: "Voice AI",
    desc: "24/7 inbound & outbound call handling for your business.",
    icon: <Mic className="w-[18px] h-[18px] stroke-primary" strokeWidth={1.8} />,
    href: "/voice-ai",
    badge: "Popular",
  },
  {
    label: "Chat Agents",
    desc: "Always-on chat support across your website and apps.",
    icon: <MessageSquare className="w-[18px] h-[18px] stroke-primary" strokeWidth={1.8} />,
    href: "/chat-ai",
  },
  {
    label: "Lead Qualification",
    desc: "Automate lead scoring and routing to your sales team.",
    icon: <Target className="w-[18px] h-[18px] stroke-primary" strokeWidth={1.8} />,
    href: "/lead-qualification",
  },
  {
    label: "Workflow Automation",
    desc: "Internal task automation for your team's daily ops.",
    icon: <Cog className="w-[18px] h-[18px] stroke-primary" strokeWidth={1.8} />,
    href: "/workflow-automation",
  },
];

const navLinks = [
  { label: "Overview", href: "/" },
  { label: "AI Agents", href: null },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

const Navbar = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAgentsOpen, setMobileAgentsOpen] = useState(false);
  const navRef = useRef<HTMLElement | null>(null);
  const dropdownTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNav = (href: string) => {
    setDropdownOpen(false);
    setMobileOpen(false);
    setMobileAgentsOpen(false);
    navigate(href);
  };

  const isActive = (href: string | null) => {
    if (!href) return location.pathname === "/";
    return location.pathname === href;
  };

  const isAgentsActive = () => {
    return location.pathname === "/voice-ai" || dropdownItems.some(d => location.pathname === d.href && d.href !== "/contact");
  };

  useEffect(() => {
    const handleClickOutside = (e: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
        setMobileOpen(false);
        setMobileAgentsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    document.addEventListener("pointerdown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const handleDropdownEnter = () => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setDropdownOpen(true);
  };

  const handleDropdownLeave = () => {
    dropdownTimer.current = setTimeout(() => setDropdownOpen(false), 180);
  };

  return (
    <header ref={navRef} className="fixed top-0 left-0 right-0 z-[100] py-3 md:py-5 transition-all duration-400">
      {/* Glass background pill */}
      <div className="absolute inset-x-3 md:inset-x-6 inset-y-1.5 md:inset-y-3 max-w-[1320px] mx-auto bg-background/70 backdrop-blur-xl border border-border/30 rounded-full shadow-[0_4px_30px_rgba(0,0,0,0.3),inset_0_1px_0_rgba(255,255,255,0.04)]">
        {/* Accent glow line */}
        <div className="absolute top-0 left-1/4 right-1/4 h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-40" />
      </div>

      <div className="max-w-[1320px] mx-auto px-4 md:px-6 lg:px-8 flex items-center justify-between gap-4 md:gap-6 lg:gap-10">
        {/* Logo */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-3 relative z-[2] pl-2 md:pl-3"
        >
          <img src={jsgLogo} alt="JSG Solutions Group" width={77} height={44} fetchPriority="high" className="h-[36px] md:h-[44px] w-auto object-contain shadow-[0_4px_16px_rgba(232,146,58,0.25)]" />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 relative z-[2]">
          {navLinks.map((link) =>
            link.href !== null ? (
              <button
                key={link.label}
                onClick={() => handleNav(link.href!)}
                className={`flex items-center gap-1.5 px-3 lg:px-5 py-2.5 lg:py-3 font-montserrat text-[11px] lg:text-[13px] font-medium rounded-xl transition-all duration-300 cursor-pointer ${
                  isActive(link.href)
                    ? "text-primary bg-primary/10"
                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
                }`}
              >
                {link.label}
              </button>
            ) : (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={handleDropdownEnter}
                onMouseLeave={handleDropdownLeave}
              >
                <button
                  className={`flex items-center gap-1.5 px-3 lg:px-5 py-2.5 lg:py-3 font-montserrat text-[11px] lg:text-[13px] font-medium rounded-xl transition-all duration-300 cursor-pointer ${
                    isAgentsActive()
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"
                  }`}
                >
                  {link.label}
                  <ChevronDown className={`h-3 w-3 transition-transform duration-300 ${dropdownOpen ? "rotate-180" : ""}`} />
                </button>

                {/* Dropdown */}
                <AnimatePresence>
                  {dropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
                      className="absolute top-[calc(100%+14px)] left-1/2 -translate-x-1/2 min-w-[380px] z-[100]"
                    >
                      {/* Bridge for cursor travel */}
                      <div className="absolute -top-[14px] left-0 right-0 h-[14px]" />

                      <div className="bg-card/95 backdrop-blur-2xl border border-border/50 rounded-xl p-4 shadow-[0_24px_60px_rgba(0,0,0,0.6),0_0_40px_rgba(232,146,58,0.05)]">
                        {/* Accent line */}
                        <div className="absolute top-0 left-[30%] right-[30%] h-px bg-gradient-to-r from-transparent via-primary to-transparent opacity-50" />

                        {/* Label */}
                        <div className="flex items-center gap-2.5 px-3 pt-2 pb-3">
                          <div className="w-4 h-px bg-primary" />
                          <span className="text-[10px] font-semibold tracking-[2px] uppercase text-primary">Our Solutions</span>
                        </div>

                        {/* Items */}
                        <div className="flex flex-col gap-1">
                          {dropdownItems.map((item) => (
                            <button
                              key={item.label}
                              onClick={() => handleNav(item.href)}
                              className="group flex items-start gap-3.5 p-3 rounded-xl text-left transition-all duration-300 hover:bg-secondary/20 relative overflow-hidden"
                            >
                              {/* Hover radial glow */}
                              <div className="absolute inset-0 bg-[radial-gradient(200px_circle_at_left_center,hsl(var(--primary)/0.08),transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                              <div className="flex-shrink-0 w-[38px] h-[38px] rounded-[10px] bg-gradient-to-br from-primary/12 to-primary/4 border border-primary/15 flex items-center justify-center transition-all duration-300 group-hover:from-primary/20 group-hover:to-primary/8 group-hover:border-primary/30 group-hover:shadow-[0_0_20px_rgba(232,146,58,0.15)]">
                                {item.icon}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between gap-2">
                                  <span className="text-sm font-semibold text-foreground flex items-center gap-2">
                                    {item.label}
                                    {item.badge && (
                                      <span className="text-[9px] font-semibold tracking-[1px] uppercase text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                                        {item.badge}
                                      </span>
                                    )}
                                  </span>
                                  <ArrowRight className="w-3.5 h-3.5 text-muted-foreground opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-primary transition-all duration-300" />
                                </div>
                                <p className="text-xs text-muted-foreground font-normal leading-relaxed mt-0.5">{item.desc}</p>
                              </div>
                            </button>
                          ))}
                        </div>

                        {/* Footer */}
                        <div className="mt-3 pt-3.5 px-3 border-t border-border/50 flex items-center justify-between gap-3">
                          <span className="text-xs text-muted-foreground">Not sure which fits your business?</span>
                          <button
                            onClick={() => handleNav("/contact")}
                            className="text-xs font-semibold text-primary flex items-center gap-1 hover:gap-2 transition-all duration-300"
                          >
                            Talk to us <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )
          )}
        </nav>

        {/* CTA */}
        <button
          onClick={() => navigate("/book-demo")}
          className="hidden md:flex items-center gap-2 px-4 lg:px-6 py-2.5 lg:py-3 font-montserrat text-[11px] lg:text-[13px] font-semibold text-primary-foreground bg-primary rounded-xl mr-1.5 relative overflow-hidden transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_24px_rgba(232,146,58,0.35)] z-[2]"
        >
          <span className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
          <span className="relative">Book a Call</span>
          <ArrowRight className="w-[13px] h-[13px] relative transition-transform duration-300 group-hover:translate-x-0.5" />
        </button>

        {/* Mobile Toggle */}
        <button
          className="md:hidden p-2 mr-2 text-foreground/80 hover:text-foreground relative z-[2]"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden border-t border-border/30 bg-card/95 backdrop-blur-xl overflow-hidden mt-3 mx-6 rounded-xl"
          >
            <div className="px-6 py-4 space-y-1">
              {navLinks.map((link) =>
                link.href !== null ? (
                  <button
                    key={link.label}
                    onClick={() => handleNav(link.href!)}
                    className={`block w-full text-left px-3 py-3 text-sm font-medium rounded-xl transition-colors ${
                      isActive(link.href) ? "text-primary bg-primary/10" : "text-foreground/80 hover:text-primary"
                    }`}
                  >
                    {link.label}
                  </button>
                ) : (
                  <div key={link.label}>
                    <button
                      onClick={() => setMobileAgentsOpen(!mobileAgentsOpen)}
                      className={`w-full flex items-center justify-between px-3 py-3 text-sm font-medium rounded-xl ${
                        isAgentsActive() ? "text-primary" : "text-foreground/80"
                      }`}
                    >
                      {link.label}
                      <ChevronDown className={`h-4 w-4 transition-transform ${mobileAgentsOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence>
                      {mobileAgentsOpen && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="pl-4 space-y-1 overflow-hidden"
                        >
                          {dropdownItems.map((item) => (
                            <button
                              key={item.label}
                              onClick={() => handleNav(item.href)}
                              className="block w-full text-left px-3 py-2.5 text-sm text-muted-foreground hover:text-primary rounded-xl transition-colors"
                            >
                              {item.label}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )
              )}

              {/* Mobile CTA */}
              <button
                onClick={() => handleNav("/book-demo")}
                className="w-full mt-2 px-4 py-3 text-sm font-semibold text-primary-foreground bg-primary rounded-xl transition-all"
              >
                Book a Call
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
